import { createRouter, createWebHistory, RouterView } from 'vue-router';
import Trans from '@/i18n/translation';
import LoginView from '@/views/LoginView.vue';
import LogoutView from '@/views/LogoutView.vue';
import { useGlobalState } from '@/store/user.store';
import ContactView from '@/views/ContactView.vue';
import HomeView from '@/views/HomeView.vue';
import ImprintView from '@/views/ImprintView.vue';
import PrivacyPolicyView from '@/views/PrivacyPolicyView.vue';
import TermsConditionsView from '@/views/TermsConditionsView.vue';
import RegisterView from '../views/RegisterView.vue';
import LandingPageView from '../views/LandingPageView.vue';
import ResearchProjectView from '../views/ResearchProjectView.vue';

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes: [
    {
      path: '/:locale?',
      component: RouterView,
      beforeEnter: Trans.routeMiddleware,
      children: [
        {
          path: '',
          name: 'home',
          component: HomeView,
          redirect: { name: 'landing' },
          children: [
            {
              path: '',
              name: 'landing',
              alias: '',
              component: LandingPageView,
            },
          ],
        },
        {
          path: 'research-project',
          name: 'research-project',
          component: ResearchProjectView,
        },
        {
          path: 'register',
          name: 'register',
          component: RegisterView,
        },
        {
          path: 'register/payment/:success',
          name: 'register-payment-return',
          component: RegisterView,
        },
        {
          path: 'login',
          name: 'login',
          component: LoginView,
        },
        {
          path: 'logout',
          name: 'logout',
          component: LogoutView,
        },

        {
          path: 'reset-password',
          name: 'reset-password',
          component: () => import('@/views/ResetPasswordView.vue'),
        },
        {
          path: 'renew-password/:token([A-Za-z0-9_-]{21,21})', // nanoid() regex
          name: 'renew-password',
          component: () => import('@/views/RenewPasswordView.vue'),
        },
        {
          path: 'dashboard',
          name: 'dashboard',
          component: () => import('@/views/dashboard/DashboardView.vue'),
          meta: { requiresAuth: true },
          children: [
            {
              path: 'company',
              name: 'company',
              children: [
                {
                  path: 'employees',
                  name: 'employees',
                  alias: 'employees',
                  component: () => import('@/views/dashboard/company/EmployeesView.vue'),
                },
                {
                  path: 'analytics',
                  name: 'company-analytics',
                  component: () => import('@/views/dashboard/company/CompanyAnalyticsView.vue'),
                },
                {
                  path: 'seats',
                  name: 'company-seats',
                  component: () => import('@/views/dashboard/company/CompanySeatsView.vue'),
                },
                {
                  path: 'subscription',
                  name: 'company-subscription',
                  component: () => import('@/views/dashboard/company/CompanySubscriptionView.vue'),
                },
                {
                  path: 'billing',
                  name: 'company-billing',
                  component: () => import('@/views/dashboard/company/CompanyInvoicesView.vue'),
                },
                {
                  path: 'details',
                  name: 'company-details',
                  component: () => import('@/views/dashboard/company/CompanyDetailsView.vue'),
                },
                {
                  path: 'overview',
                  name: 'company-course-overview',
                  component: () => import('@/views/dashboard/company/CourseOverview.vue'),
                },
              ],
            },
            {
              path: 'training',
              name: 'training',
              children: [
                {
                  path: 'course/:courseId',
                  name: 'training-course',
                  redirect: {
                    name: 'training-course-overview',
                  },
                  children: [
                    {
                      path: 'overview',
                      name: 'training-course-overview',
                      component: () => import('@/views/dashboard/training/EmployeeCourseOverview.vue'),
                    },
                  ],
                },
              ],
            },
            {
              path: 'account',
              name: 'account',
              children: [
                {
                  path: 'subscription',
                  name: 'account-subscription',
                  component: () => import('@/views/dashboard/account/AccountSubscriptionView.vue'),
                },
                {
                  path: 'payments',
                  name: 'account-payments',
                  component: () => import('@/views/dashboard/account/AccountPaymentsView.vue'),
                },
                {
                  path: 'details',
                  name: 'account-details',
                  component: () => import('@/views/dashboard/account/AccountDetailsView.vue'),
                },
              ],
            },
            {
              path: 'subscription',
              name: 'subscription',
              component: () => import('@/views/dashboard/company/CompanySubscriptionView.vue'),
            },
            {
              path: 'upgrade-subscription',
              name: 'upgrade-subscription',
              component: () => import('@/components/dashboard/UpgradeSubscriptionComponent.vue'),
            },
          ],
        },
        {
          path: 'imprint',
          name: 'imprint',
          component: ImprintView,
        },
        {
          path: 'privacy-policy',
          name: 'privacy-policy',
          component: PrivacyPolicyView,
        },
        {
          path: 'terms-and-conditions',
          name: 'terms-and-conditions',
          component: TermsConditionsView,
        },
        {
          path: 'contact',
          name: 'contact',
          component: ContactView,
        },
      ],
    },
    { path: '/:pathMatch(.*)*', redirect: 'home' },
  ],
  scrollBehavior(to /* , from, savedPosition */) {
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
      };
    }

    return {
      top: 0,
      behavior: 'smooth',
    };
  },
});

router.beforeResolve((to/* , from */) => {
  if (!to.meta.requiresAuth) return null;
  if (to.name === 'login') return null;

  const { isAuthenticated } = useGlobalState();
  if (isAuthenticated()) return null;

  return {
    name: 'login',
    query: { redirect: to.fullPath },
  };
});

export default router;
